import moment from 'moment';
import { LocaleCode } from 'acscp/translations';
import { getDefaultTimeFormatForLocaleOrFallback } from './helpers/dateTimeHelper';

// ISO 8601
export const isoDateFormat = 'YYYY-MM-DD';
export const isoTimeFormat = 'HH:mm';

export class DateTime {
    private moment: moment.Moment;

    static Now(): DateTime {
        return new DateTime(moment().toISOString());
    }

    static FromDate(date: Date): DateTime {
        return new DateTime(moment(date).toISOString());
    }

    constructor(readonly dateTimeString: string, inputFormat?: moment.MomentFormatSpecification) {
        this.moment = inputFormat !== undefined
            ? moment(dateTimeString, inputFormat)
            : moment(dateTimeString);
    }

    hasTime = () => {
        // checks if dateTimeString contains time eg '2017-11-05T10:00:00.000'
        const timeRegex = /T\d{2}:\d{2}(:\d{2}(.\d{1,3})?)?Z?$/;
        return timeRegex.test(this.dateTimeString);
    }

    hasDate = () => {
        // checks if dateTimeString contains day of the month eg '2017-11-05'
        const dateRegex = /^\d{4}-\d{2}-\d{2}/;
        return dateRegex.test(this.dateTimeString);
    }

    isValid = () => {
        // checks if dateTimeString has one of the following valid formats:
        // 'YYYY-MM', 'YYYY-MM-DD', 'YYYY-MMTHH:MM', 'YYYY-MM-DDTHH:MM'
        // 'YYYY-MM-DDTHH:MM:ss', 'YYYY-MMTHH:MM:ss',
        // 'YYYY-MM-DDTHH:MM:ss.SSS', 'YYYY-MMTHH:MM:ss.SSS' or
        // 'YYYY-MMTHH:MM:ss.SSSZ'
        const regex = /^\d{4}-\d{2}(-\d{2})?(T\d{2}:\d{2}(:\d{2}(.\d{1,3}(Z)?)?)?)?$/;
        return regex.test(this.dateTimeString) && this.moment.isValid();
    }

    /** Difference between datetimes in milliseconds */
    diff = (dateTime: DateTime, inDays: boolean = false) => {
        // If a dateTime is missing the day, the missing day will default to 1.
        // If a dateTime is missing the time, the missing time will default to 00:00.
        const unit = inDays ? 'days' : undefined;
        return this.moment.diff(dateTime.moment, unit);
    }

    add = (amount?: moment.DurationInputArg1, unit?: moment.DurationInputArg2): DateTime => {
        return new DateTime(this.moment.clone().add(amount, unit).toISOString());
    }

    getDate = () => this.moment.toDate();

    getLocallyFormattedTime = (locale?: LocaleCode) =>
        this.hasTime() ? this.moment.format(getDefaultTimeFormatForLocaleOrFallback(locale)) : null

    isToday = () =>
        this.hasDate() && moment().isSame(this.moment, 'day')

    isTomorrow = () =>
        this.hasDate() && moment().add(1, 'days').isSame(this.moment, 'day')

    // This should not be used for any localisable dates
    format = (dateFormat: string) =>
        this.moment.format(dateFormat)
}
